import { useEffect, useMemo, useState } from "react";

import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { cn } from "../lib/utils.js";
import { StatusChip } from "./ui/status-chip.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table.js";
import { Skeleton } from "./Skeleton.js";
import {
  AttendanceLegend, PRESENT_STATUSES, TONE_BY_STATUS, cellTitle,
  type GridCell, type MonthlyGrid,
} from "./attendance/attendanceCells.js";

/** Every month row is 31 wide so the day columns line up across rows of different
 *  lengths; a month's missing tail (and any date outside the range) stays blank. */
const DAYS_IN_ROW = 31;
const DAY_COLUMNS = Array.from({ length: DAYS_IN_ROW }, (_, i) => i + 1);

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

interface MonthRow {
  key: string;
  label: string;
  /** Indexed by day-of-month - 1; null where the date is outside the range. */
  slots: Array<{ day: string; cell: GridCell } | null>;
  present: number;
  absent: number;
}

/**
 * `days` and `cells` arrive as parallel arrays over the flat range. Fold them into
 * one row per calendar month, each slotted by day-of-month, and count that row's
 * own P / A -- the payload's `totals` cover the whole range, not a single month.
 */
function pivotByMonth(days: string[], cells: GridCell[]): MonthRow[] {
  const rows = new Map<string, MonthRow>();
  days.forEach((day, i) => {
    const cell = cells[i];
    if (!cell) return;
    const ym = day.slice(0, 7);
    let row = rows.get(ym);
    if (!row) {
      const month = Number(ym.slice(5, 7)) - 1;
      row = {
        key: ym,
        label: `${MONTH_LABELS[month]} ${ym.slice(0, 4)}`,
        slots: Array.from({ length: DAYS_IN_ROW }, () => null),
        present: 0,
        absent: 0,
      };
      rows.set(ym, row);
    }
    row.slots[Number(day.slice(8, 10)) - 1] = { day, cell };
    if (PRESENT_STATUSES.has(cell.status)) row.present += 1;
    else if (cell.status === "ABSENT") row.absent += 1;
  });
  return [...rows.values()].sort((a, b) => a.key.localeCompare(b.key));
}

export interface RegisterCorrection { date: string; status: string }

/** Month rows the range will produce -- known before the request returns, so the
 *  skeleton reserves the finished height and nothing shifts when data lands. */
function monthsInRange(from: string, to: string): number {
  if (!from || !to || from > to) return 0;
  const [fy, fm] = [Number(from.slice(0, 4)), Number(from.slice(5, 7))];
  const [ty, tm] = [Number(to.slice(0, 4)), Number(to.slice(5, 7))];
  return (ty - fy) * 12 + (tm - fm) + 1;
}

/**
 * One person's attendance as a month x day-of-month matrix. It reads the same
 * /attendance/monthly-grid payload the All Team grid uses and shares the status,
 * tone, hover-text and legend vocabulary with that grid.
 *
 * Without `employeeId` it is My History's own register (`self=true`, which every
 * role may call). With one it is that employee's register, which the API gates by
 * the same rule as their daily history: HR, themselves, or their manager.
 *
 * Selection is controlled: the parent owns the chosen day so it can render its
 * own list row underneath, rather than this view inventing a second day layout.
 */
export function AttendanceRegister({
  from, to, corrections, selectedDay, onSelectDay, employeeId,
}: {
  from: string;
  to: string;
  corrections: RegisterCorrection[];
  selectedDay: string | null;
  onSelectDay: (day: string | null) => void;
  /** Whose register to show. Omitted means the viewer's own. */
  employeeId?: string | undefined;
}) {
  const [grid, setGrid] = useState<MonthlyGrid | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!from || !to || from > to) { setLoading(false); setGrid(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ from, to, granularity: "day", page: "1", limit: "1" });
    if (employeeId) params.set("employeeId", employeeId);
    else params.set("self", "true");
    api.get<MonthlyGrid>(`/attendance/monthly-grid?${params}`)
      .then((r) => { if (!cancelled) setGrid(r.data); })
      .catch((err) => {
        if (cancelled) return;
        logError("AttendanceRegister.load", err);
        setError(errMsg(err) ?? "Failed to load the register");
        setGrid(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to, employeeId]);

  const monthRows = useMemo(
    () => (grid?.rows[0] ? pivotByMonth(grid.days, grid.rows[0].cells) : []),
    [grid],
  );

  // A day carries a dot when its correction request is still awaiting a decision or
  // has been approved; a rejected one already shows the shared corner marker.
  const dotByDate = useMemo(() => {
    const map = new Map<string, "pending" | "approved">();
    for (const c of corrections) {
      const key = c.date.slice(0, 10);
      if (c.status === "PENDING") map.set(key, "pending");
      else if (c.status === "APPROVED") map.set(key, "approved");
    }
    return map;
  }, [corrections]);

  return (
    <div className="flex flex-col gap-3">
      {error && (
        <div className="rounded-md bg-[var(--color-error-bg)] px-3 py-2 text-[13px] text-[var(--color-error-fg)]">{error}</div>
      )}

      <AttendanceLegend>
        <span className="flex items-center gap-1.5 text-xs text-surface-600">
          <StatusChip tone="off" size="legend" dot="pending" />
          Awaiting approval
        </span>
        <span className="flex items-center gap-1.5 text-xs text-surface-600">
          <StatusChip tone="off" size="legend" dot="approved" />
          Approved
        </span>
        <span className="ml-auto text-xs text-surface-600">
          {selectedDay ? "Showing the selected day below." : "Select any day to see its details below."}
        </span>
      </AttendanceLegend>

      <div className="overflow-x-auto rounded-md border border-surface-200">
        {loading ? (
          <Table className="w-max min-w-full">
            <TableHeader>
              <TableRow className="bg-surface-50">
                <TableHead className="sticky left-0 z-10 bg-surface-50"><div className="w-[88px] truncate">Month</div></TableHead>
                {DAY_COLUMNS.map((d) => (
                  <TableHead key={d} className="w-7 px-0 text-center font-semibold">{String(d).padStart(2, "0")}</TableHead>
                ))}
                <TableHead className="sticky right-9 z-10 w-9 bg-surface-50 px-2 text-center">P</TableHead>
                <TableHead className="sticky right-0 z-10 w-9 bg-surface-50 px-2 text-center">A</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: Math.max(1, monthsInRange(from, to)) }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell className="sticky left-0 z-10 bg-surface-0">
                    <Skeleton width={70} height={14} />
                  </TableCell>
                  {DAY_COLUMNS.map((d) => (
                    <TableCell key={d} className="w-7 px-0 py-1 text-center">
                      <Skeleton width={24} height={24} borderRadius="4px" style={{ margin: "0 auto" }} />
                    </TableCell>
                  ))}
                  <TableCell className="sticky right-9 z-10 w-9 bg-surface-0 px-2"><Skeleton width={14} height={14} /></TableCell>
                  <TableCell className="sticky right-0 z-10 w-9 bg-surface-0 px-2"><Skeleton width={14} height={14} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : monthRows.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-surface-500">No attendance recorded for this period.</div>
        ) : (
          <Table className="w-max min-w-full">
            <TableHeader>
              <TableRow className="bg-surface-50">
                {/* Sticky cells need an opaque background of their own -- they are
                    transparent by default and would show the day columns through.
                    P and A are pinned right so the totals stay put while the 31 day
                    columns scroll; P sits one A-width (w-9 = 36px) in from the edge. */}
                <TableHead className="sticky left-0 z-10 bg-surface-50">
                  <div className="w-[88px] truncate">Month</div>
                </TableHead>
                {DAY_COLUMNS.map((d) => (
                  <TableHead key={d} className="w-7 px-0 text-center font-semibold">
                    {String(d).padStart(2, "0")}
                  </TableHead>
                ))}
                <TableHead className="sticky right-9 z-10 w-9 bg-surface-50 px-2 text-center">P</TableHead>
                <TableHead className="sticky right-0 z-10 w-9 bg-surface-50 px-2 text-center">A</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {monthRows.map((row) => (
                <TableRow key={row.key} className="hover:bg-surface-50">
                  <TableCell className="sticky left-0 z-10 bg-surface-0 font-medium">
                    <div className="w-[88px] truncate">{row.label}</div>
                  </TableCell>
                  {DAY_COLUMNS.map((d) => {
                    const slot = row.slots[d - 1];
                    return (
                      <TableCell key={d} className="w-7 px-0 py-1 text-center">
                        {slot && (
                          <StatusChip
                            tone={TONE_BY_STATUS[slot.cell.status]}
                            marker={slot.cell.rejected}
                            dot={dotByDate.get(slot.day)}
                            title={cellTitle(slot.cell, slot.day)}
                            onClick={() => onSelectDay(selectedDay === slot.day ? null : slot.day)}
                            className={cn(
                              "cursor-pointer",
                              selectedDay === slot.day && "ring-2 ring-brand-600 ring-offset-1 ring-offset-[var(--color-surface-0)]",
                            )}
                          >
                            {slot.cell.label}
                          </StatusChip>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell className="sticky right-9 z-10 w-9 bg-surface-0 px-2 text-center text-[12px] font-semibold">{row.present}</TableCell>
                  <TableCell className="sticky right-0 z-10 w-9 bg-surface-0 px-2 text-center text-[12px] font-semibold text-[var(--color-error-fg)]">{row.absent}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
